import { Link } from 'react-router-dom';

import { StripeWidget } from '@/components/organizer/stripe-widget';
import { SubscriptionWidget } from '@/components/organizer/subscription-widget';
import { ROUTES } from '@/config/routes.ts';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { getMfaStatusApi } from '@/lib/auth/authApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
} from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Plus,
  ShieldCheck,
  Trophy,
  Users2,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

// ─── Helpers ─────────────────────────────────────────────
const STATUS_CONFIG: Record<CompetitionStatus, { label: string; dot: string; card: string }> = {
  [CompetitionStatus.DRAFT]: { label: 'Brouillon', dot: 'bg-slate-400', card: 'border-slate-200' },
  [CompetitionStatus.REGISTRATION_OPEN]: {
    label: 'Inscriptions ouvertes',
    dot: 'bg-emerald-500',
    card: 'border-emerald-200',
  },
  [CompetitionStatus.REGISTRATION_CLOSED]: {
    label: 'Inscriptions fermées',
    dot: 'bg-amber-500',
    card: 'border-amber-200',
  },
  [CompetitionStatus.IN_PROGRESS]: { label: 'En cours', dot: 'bg-blue-500', card: 'border-blue-200' },
  [CompetitionStatus.FINISHED]: { label: 'Terminé', dot: 'bg-slate-400', card: 'border-slate-100' },
  [CompetitionStatus.CANCELLED]: { label: 'Annulé', dot: 'bg-red-400', card: 'border-red-100' },
  [CompetitionStatus.DISABLED_BY_ADMIN]: { label: 'Désactivé', dot: 'bg-red-500', card: 'border-red-200' },
};

function CompetitionCard({ c }: { c: Competition }) {
  const cfg = STATUS_CONFIG[c.status];
  const progress = c.maxTeams ? Math.round((c.currentTeams / c.maxTeams) * 100) : 0;
  return (
    <div className={`competition-card rounded-2xl border bg-white p-5 transition-shadow hover:shadow-lg ${cfg.card}`}>
      <div className='mb-3 flex items-start justify-between'>
        <div className='flex-1'>
          <div className='mb-2 flex flex-wrap items-center gap-2'>
            <span className='flex items-center gap-1.5 text-xs font-semibold text-slate-600'>
              <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
              {cfg.label}
            </span>
            <span className='rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700'>
              {c.type ? TYPE_LABELS[c.type] : '—'}
            </span>
            <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600'>
              {c.level ? LEVEL_LABELS[c.level] : '—'}
            </span>
          </div>
          <h3 className='font-bold text-slate-900'>{c.name}</h3>
          <p className='mt-1 text-sm text-slate-500'>
            {c.date ? formatDateAbbreviatedFr(c.date) : '—'}
            {(c.venue ?? c.city) && ` · ${c.venue ?? c.city}`}
          </p>
        </div>
      </div>
      <div className='grid grid-cols-3 gap-4 border-t border-slate-100 pt-3'>
        <div>
          <p className='text-xs text-slate-500'>Équipes</p>
          <p className='font-bold text-slate-900'>
            {c.currentTeams}/{c.maxTeams ?? '?'}
          </p>
        </div>
        <div>
          <p className='text-xs text-slate-500'>Remplissage</p>
          <p className='font-bold text-slate-900'>{c.maxTeams ? `${progress}%` : '—'}</p>
        </div>
        <div>
          <p className='text-xs text-slate-500'>Prix/équipe</p>
          <p className='font-bold text-slate-900'>{c.price ? `${(c.price / 100).toFixed(0)}€` : 'Gratuit'}</p>
        </div>
      </div>
      {c.maxTeams && (
        <div className='mt-3 h-1.5 w-full rounded-full bg-slate-200'>
          <div
            className={`h-1.5 rounded-full transition-all ${progress >= 90 ? 'bg-red-500' : progress >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────
export default function OrganizerDashboard() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const displayName = user?.firstName ?? user?.username ?? 'Organisateur';
  const orgMembership = user?.organizationsAdmin[0];
  const orgId = orgMembership?.organizationId;

  const { data: mfaStatus } = useQuery({
    queryKey: queryKeys.mfa.status(),
    queryFn: getMfaStatusApi,
    enabled: isAuthenticated,
  });

  const shouldShowMfaBanner = mfaStatus?.totpEnabled === false;

  const { data: org } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId!),
    queryFn: () => getOrganizationApi(orgId!),
    enabled: !!orgId && isAuthenticated,
  });

  const {
    data: compsData,
    isLoading: compsLoading,
    isError: compsError,
    refetch: compsRefetch,
  } = useQuery({
    queryKey: queryKeys.competitions.list({ organizationId: orgId, limit: 10 }),
    queryFn: () => getCompetitionsApi({ organizationId: orgId, limit: 10 }),
    enabled: !!orgId && isAuthenticated,
  });

  const competitions = compsData?.data ?? [];
  const total = compsData?.meta?.total ?? 0;

  // Dériver des stats depuis la liste
  const activeCount = competitions.filter(
    c => c.status === CompetitionStatus.REGISTRATION_OPEN || c.status === CompetitionStatus.IN_PROGRESS,
  ).length;
  const draftCount = competitions.filter(c => c.status === CompetitionStatus.DRAFT).length;
  const totalTeams = competitions.reduce((acc, c) => acc + c.currentTeams, 0);

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-8'>
        {/* Bandeau sécurité MFA */}
        {shouldShowMfaBanner && (
          <div className='mb-6 flex items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4'>
            <AlertTriangle className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600' />
            <div className='flex-1'>
              <p className='font-semibold text-amber-800'>Sécurisez votre compte</p>
              <p className='mt-0.5 text-sm text-amber-700'>
                Activez l&apos;application d&apos;authentification (TOTP) et générez vos codes de secours pour protéger
                votre accès.
              </p>
            </div>
            <Link
              to={ROUTES.PROFILE_SECURITY}
              className='flex-shrink-0 flex items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-2 text-xs font-bold text-white hover:bg-amber-700 transition-colors'
            >
              <ShieldCheck className='h-3.5 w-3.5' />
              Activer
            </Link>
          </div>
        )}

        <PageHeader
          title={`Bonjour, ${displayName}`}
          subtitle={org ? `${org.name} — tableau de bord` : 'Votre tableau de bord'}
          actions={
            <Link
              to={ROUTES.COMPETITION_CREATE}
              className='flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700'
            >
              <Plus className='h-5 w-5' />
              Nouveau concours
            </Link>
          }
        />

        {/* Stats */}
        <div className='mb-8 grid grid-cols-2 gap-4 md:grid-cols-4'>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100'>
              <Trophy className='h-5 w-5 text-blue-600' />
            </div>
            <p className='text-2xl font-extrabold text-slate-900'>{total}</p>
            <p className='mt-1 text-sm text-slate-500'>Concours au total</p>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100'>
              <CheckCircle className='h-5 w-5 text-emerald-600' />
            </div>
            <p className='text-2xl font-extrabold text-slate-900'>{activeCount}</p>
            <p className='mt-1 text-sm text-slate-500'>Actifs (ouverts + en cours)</p>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100'>
              <Clock className='h-5 w-5 text-amber-600' />
            </div>
            <p className='text-2xl font-extrabold text-slate-900'>{draftCount}</p>
            <p className='mt-1 text-sm text-slate-500'>Brouillons</p>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100'>
              <Users2 className='h-5 w-5 text-purple-600' />
            </div>
            <p className='text-2xl font-extrabold text-slate-900'>{totalTeams}</p>
            <p className='mt-1 text-sm text-slate-500'>Équipes inscrites</p>
          </div>
        </div>

        {/* Main */}
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          {/* Concours */}
          <div className='lg:col-span-2'>
            <div className='mb-6 flex items-center justify-between'>
              <h2 className='text-xl font-bold text-slate-900'>Mes concours</h2>
              <Link
                to={ROUTES.COMPETITION_LIST}
                className='flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700'
              >
                Voir tout <ArrowRight className='h-4 w-4' />
              </Link>
            </div>

            {compsLoading ? (
              <div className='space-y-4'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className='h-36 animate-pulse rounded-2xl bg-slate-200' />
                ))}
              </div>
            ) : compsError ? (
              <QueryErrorState onRetry={() => void compsRefetch()} />
            ) : competitions.length === 0 ? (
              <div className='rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center'>
                <Trophy className='mx-auto mb-3 h-10 w-10 text-slate-300' />
                <p className='text-sm text-slate-500'>Aucun concours pour l&apos;instant</p>
                <Link
                  to={ROUTES.COMPETITION_CREATE}
                  className='mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700'
                >
                  <Plus className='h-4 w-4' /> Créer un concours
                </Link>
              </div>
            ) : (
              <div className='space-y-4'>
                {competitions.slice(0, 5).map(c => (
                  <CompetitionCard key={c.id} c={c} />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className='space-y-6'>
            {/* Actions rapides */}
            <div className='rounded-2xl border border-slate-200 bg-white p-6'>
              <h3 className='mb-4 font-bold text-slate-900'>Actions rapides</h3>
              <div className='space-y-2'>
                <Link
                  to={ROUTES.COMPETITION_CREATE}
                  className='flex w-full items-center gap-3 rounded-xl p-3 transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100'>
                    <Plus className='h-5 w-5 text-blue-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Créer un concours</p>
                    <p className='text-xs text-slate-500'>Nouveau tournoi</p>
                  </div>
                </Link>
                <Link
                  to={ROUTES.COMPETITION_LIST}
                  className='flex w-full items-center gap-3 rounded-xl p-3 transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100'>
                    <FileText className='h-5 w-5 text-emerald-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Mes concours</p>
                    <p className='text-xs text-slate-500'>Gérer les tournois</p>
                  </div>
                </Link>
                <Link
                  to={ROUTES.ORGANIZER_INSCRIPTIONS}
                  className='flex w-full items-center gap-3 rounded-xl p-3 transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100'>
                    <Users2 className='h-5 w-5 text-purple-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Inscriptions</p>
                    <p className='text-xs text-slate-500'>Gérer les équipes</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Stripe Connect */}
            {org && (
              <StripeWidget
                orgId={org.id}
                stripeAccountStatus={org.stripeAccountStatus}
                stripeTestMode={org.stripeTestMode}
                sirenMissing={!org.siren}
              />
            )}

            {/* Abonnement */}
            {org && (
              <SubscriptionWidget
                orgId={org.id}
                plan={org.plan}
                subscriptionStatus={org.subscriptionStatus}
                subscriptionEndDate={org.subscriptionEndDate}
                subscriptionInterval={org.subscriptionInterval}
                cancelAtPeriodEnd={org.cancelAtPeriodEnd}
                commissionCreditRemainingCents={org.commissionCreditRemainingCents}
              />
            )}

            {/* Organisation info */}
            {org && (
              <div className='rounded-2xl border border-slate-200 bg-white p-6'>
                <h3 className='mb-4 flex items-center gap-2 font-bold text-slate-900'>
                  <Briefcase className='h-5 w-5 text-slate-600' />
                  Mon organisation
                </h3>
                <div className='space-y-3'>
                  <div>
                    <p className='text-xs text-slate-500'>Nom</p>
                    <p className='font-semibold text-slate-900'>{org.name}</p>
                  </div>
                  {org.city && (
                    <div>
                      <p className='text-xs text-slate-500'>Ville</p>
                      <p className='font-semibold text-slate-900'>{org.city}</p>
                    </div>
                  )}
                  {org.email && (
                    <div>
                      <p className='text-xs text-slate-500'>Email</p>
                      <p className='font-semibold text-slate-900'>{org.email}</p>
                    </div>
                  )}
                  <div>
                    <p className='text-xs text-slate-500'>Membre depuis</p>
                    <p className='font-semibold text-slate-900'>{formatDateAbbreviatedFr(org.createdAt)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Prochains événements */}
            {competitions.filter(
              c => c.status !== CompetitionStatus.FINISHED && c.status !== CompetitionStatus.CANCELLED,
            ).length > 0 && (
              <div className='rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 p-6 text-white'>
                <h3 className='mb-4 flex items-center gap-2 font-bold'>
                  <Calendar className='h-5 w-5' />
                  Prochains événements
                </h3>
                <div className='space-y-3'>
                  {competitions
                    .filter(c => c.status !== CompetitionStatus.FINISHED && c.status !== CompetitionStatus.CANCELLED)
                    .slice(0, 3)
                    .map(c => (
                      <div key={c.id} className='rounded-xl bg-white/10 p-3 backdrop-blur'>
                        <p className='text-xs text-blue-100'>{c.date ? formatDateAbbreviatedFr(c.date) : '—'}</p>
                        <p className='mt-0.5 text-sm font-semibold'>{c.name}</p>
                        <p className='mt-0.5 text-xs text-blue-100'>
                          {c.currentTeams} équipe{c.currentTeams > 1 ? 's' : ''} inscrite{c.currentTeams > 1 ? 's' : ''}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
